/**
 * WebsiteEditPage — /websites/:id/edit
 *
 * Simple owner-only editor. Tabbed layout: Basics, Content, Design,
 * Media & Videos, Contact, Publish.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Save, ExternalLink, Eye, EyeOff, Sparkles, ArrowLeft,
  Image as ImageIcon, Video, Palette, FileText, Mail, MessageSquare, Layers,
  Utensils, Plus, Trash2,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import VideoLinkManager from "@/components/VideoLinkManager";

const FOOD_TEMPLATES = new Set(["restaurant", "cafe", "bakery", "food_truck", "grocery"]);

const TABS = [
  { id: "basics",  label: "Basics",  Icon: Sparkles },
  { id: "content", label: "Content", Icon: FileText },
  { id: "design",  label: "Design",  Icon: Palette },
  { id: "media",   label: "Media",   Icon: ImageIcon },
  { id: "menu",    label: "Menu",    Icon: Utensils, foodOnly: true },
  { id: "contact", label: "Contact", Icon: Mail },
  { id: "inbox",   label: "Inbox",   Icon: MessageSquare },
];

export default function WebsiteEditPage() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [site, setSite] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("basics");
  const [msgs, setMsgs] = useState([]);

  const load = () => {
    http.get(`/websites/id/${id}/preview`).then(r => setSite(r.data)).catch(e => {
      toast.error(e?.response?.data?.detail || "Could not load website.");
      nav("/my/websites");
    });
  };

  useEffect(() => {
    if (!authLoading && !user) { nav(`/login?redirect=${encodeURIComponent(window.location.pathname)}`, { replace: true }); return; }
    if (user) load();
    http.get("/websites/templates").then(r => setCatalogue(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id, user, authLoading]);

  useEffect(() => {
    if (tab === "inbox" && id) {
      http.get(`/websites/${id}/messages`).then(r => setMsgs(r.data.items || [])).catch(() => setMsgs([]));
    }
  }, [tab, id]);

  const set = (patch) => { setSite(s => ({ ...s, ...patch })); setDirty(true); };
  const setContact = (patch) => set({ contact: { ...(site.contact || {}), ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        template: site.template, theme: site.theme, layout: site.layout,
        name: site.name, tagline: site.tagline, description: site.description,
        logo_url: site.logo_url, hero_image_url: site.hero_image_url,
        photo_urls: site.photo_urls || [],
        video_urls: (site.video_links || []).map(v => v.watch_url).filter(Boolean),
        contact: site.contact || {},
        pages: site.pages || [],
        modules: site.modules || {},
        menu: site.menu || [],
      };
      await http.patch(`/websites/${site.id}`, body);
      toast.success("Saved");
      setDirty(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    if (!site) return;
    try {
      if (site.status === "published") await http.post(`/websites/${site.id}/unpublish`);
      else                              await http.post(`/websites/${site.id}/publish`);
      toast.success(site.status === "published" ? "Unpublished" : "Published!");
      load();
    } catch { toast.error("Failed"); }
  };

  if (!site) return <div className="p-12 opacity-60 inline-flex gap-2 items-center"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

  const pubUrl = `/sites/${site.slug}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="website-edit-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Link to="/my/websites" className="text-sm opacity-70 hover:opacity-100 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />All websites</Link>
        <span className="text-xs font-mono opacity-60">·</span>
        <h1 className="font-display text-2xl truncate">{site.name}</h1>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${site.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{site.status}</span>
        <div className="ml-auto flex items-center gap-2">
          {site.status === "published" && (
            <a href={pubUrl} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="open-public">
              <ExternalLink className="w-3.5 h-3.5" />View
            </a>
          )}
          <button onClick={togglePublish} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="toggle-publish">
            {site.status === "published" ? <><EyeOff className="w-3.5 h-3.5" />Unpublish</> : <><Eye className="w-3.5 h-3.5" />Publish</>}
          </button>
          <button onClick={save} disabled={!dirty || saving} className="wofy-btn-primary inline-flex items-center gap-1 disabled:opacity-40 text-xs" data-testid="save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap mb-6 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.filter(t => !t.foodOnly || FOOD_TEMPLATES.has(site.template)).map(({ id: t, label, Icon }) => (
          <button key={t} onClick={() => setTab(t)}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 ${tab === t ? "border-black" : "border-transparent opacity-70 hover:opacity-100"}`}
            data-testid={`tab-${t}`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {tab === "basics" && (
        <div className="space-y-4" data-testid="tab-basics-content">
          <Field label="Site name"><input className="wofy-input" value={site.name || ""} onChange={e => set({ name: e.target.value })} data-testid="input-name" /></Field>
          <Field label="Tagline"><input className="wofy-input" value={site.tagline || ""} onChange={e => set({ tagline: e.target.value })} data-testid="input-tagline" /></Field>
          <Field label="Description">
            <textarea rows={5} className="wofy-input" value={site.description || ""} onChange={e => set({ description: e.target.value })} data-testid="input-description" />
          </Field>
        </div>
      )}

      {tab === "content" && (
        <PagesEditor pages={site.pages || []} onChange={pages => set({ pages })} />
      )}

      {tab === "design" && (
        <div className="space-y-6" data-testid="tab-design-content">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Template</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {(catalogue?.templates || []).map(t => (
                <button key={t.slug} onClick={() => set({ template: t.slug })}
                  className={`wofy-card !p-3 text-left ${site.template === t.slug ? "ring-2 ring-black" : ""}`}
                  data-testid={`design-template-${t.slug}`}>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs opacity-70">{t.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Colour theme</div>
            <div className="flex flex-wrap gap-2">
              {(catalogue?.themes || []).map(t => (
                <button key={t.slug} onClick={() => set({ theme: t.slug })}
                  className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border ${site.theme === t.slug ? "ring-2 ring-black" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`design-theme-${t.slug}`}>
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: t.primary }} />
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: t.accent }} />
                  <span className="capitalize">{t.slug}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Layout</div>
            <div className="flex flex-wrap gap-2">
              {(catalogue?.layouts || []).map(l => (
                <button key={l.slug} onClick={() => set({ layout: l.slug })}
                  className={`text-sm px-3 py-1.5 rounded-full border ${site.layout === l.slug ? "ring-2 ring-black" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`design-layout-${l.slug}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5" />Modules</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(site.modules || {}).map(([k, v]) => (
                <label key={k} className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border cursor-pointer ${v ? "bg-black/5" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}>
                  <input type="checkbox" checked={v} onChange={() => set({ modules: { ...site.modules, [k]: !v } })}
                    className="w-3 h-3" data-testid={`module-${k}`} />
                  {k.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "media" && (
        <div className="space-y-4" data-testid="tab-media-content">
          <Field label="Logo URL"><input className="wofy-input" value={site.logo_url || ""} onChange={e => set({ logo_url: e.target.value })} data-testid="input-logo" /></Field>
          <Field label="Hero image URL"><input className="wofy-input" value={site.hero_image_url || ""} onChange={e => set({ hero_image_url: e.target.value })} data-testid="input-hero" /></Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1 inline-flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" />Upload photos (URLs, one per line)
              </div>
              <textarea rows={6} className="wofy-input font-mono text-xs"
                value={(site.photo_urls || []).join("\n")}
                onChange={e => set({ photo_urls: e.target.value.split(/\n/).map(s => s.trim()).filter(Boolean) })}
                placeholder="https://…jpg" data-testid="input-photos" />
              <p className="text-xs opacity-60 mt-1">Up to 24 photos rendered in the gallery section.</p>
            </div>
            <div>
              <VideoLinkManager
                value={(site.video_links || []).map(v => v.watch_url)}
                onChange={(urls) => set({ video_links: urls.map(u => ({ watch_url: u })) })}
                max={12}
                helper="Paste YouTube or Vimeo links. Embedded in your Videos section."
              />
            </div>
          </div>
        </div>
      )}

      {tab === "menu" && (
        <MenuEditor menu={site.menu || []} onChange={menu => set({ menu })} />
      )}

      {tab === "contact" && (
        <div className="space-y-4" data-testid="tab-contact-content">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Contact email"><input type="email" className="wofy-input" value={site.contact?.email || ""} onChange={e => setContact({ email: e.target.value })} data-testid="input-email" /></Field>
            <Field label="Contact phone"><input className="wofy-input" value={site.contact?.phone || ""} onChange={e => setContact({ phone: e.target.value })} data-testid="input-phone" /></Field>
          </div>
          <Field label="Address"><input className="wofy-input" value={site.contact?.address || ""} onChange={e => setContact({ address: e.target.value })} data-testid="input-address" /></Field>
          <Field label="Website"><input className="wofy-input" value={site.contact?.website || ""} onChange={e => setContact({ website: e.target.value })} data-testid="input-website" /></Field>
          <HoursEditor hours={site.contact?.hours || {}} onChange={hours => setContact({ hours })} />
        </div>
      )}

      {tab === "inbox" && (
        <div className="space-y-3" data-testid="tab-inbox-content">
          <p className="text-sm opacity-70">Messages sent via your public contact form.</p>
          {msgs.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">No messages yet.</div>
          ) : (
            <ul className="space-y-2">
              {msgs.map(m => (
                <li key={m.id} className="wofy-card !p-3" data-testid={`msg-${m.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <b>{m.name}</b>
                    {m.email && <span className="opacity-70">· {m.email}</span>}
                    {m.phone && <span className="opacity-70">· {m.phone}</span>}
                    <span className="ml-auto text-[10px] opacity-60">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm whitespace-pre-line opacity-90">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}

function PagesEditor({ pages, onChange }) {
  const [active, setActive] = useState(pages[0]?.id || null);
  const cur = pages.find(p => p.id === active) || pages[0];

  const patchCur = (patch) => {
    onChange(pages.map(p => p.id === cur.id ? { ...p, ...patch } : p));
  };

  return (
    <div className="grid gap-4 md:grid-cols-[220px_1fr]" data-testid="tab-content-content">
      <ul className="space-y-1">
        {pages.map(p => (
          <li key={p.id}>
            <button onClick={() => setActive(p.id)}
              className={`w-full text-left text-sm px-3 py-2 rounded ${active === p.id ? "bg-black text-white" : "hover:bg-black/5"}`}
              data-testid={`page-tab-${p.slug}`}>
              {p.title}
            </button>
          </li>
        ))}
      </ul>
      {cur ? (
        <div className="space-y-3">
          <Field label="Page title"><input className="wofy-input" value={cur.title} onChange={e => patchCur({ title: e.target.value })} data-testid="page-title" /></Field>
          <Field label="Body (Markdown — # heading, ## sub, - bullets)">
            <textarea rows={14} className="wofy-input font-mono text-sm" value={cur.body_markdown || ""} onChange={e => patchCur({ body_markdown: e.target.value })} data-testid="page-body" />
          </Field>
        </div>
      ) : (
        <div className="opacity-60 text-sm">This site has no pages yet.</div>
      )}
    </div>
  );
}


const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function HoursEditor({ hours, onChange }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Business hours</div>
      <div className="space-y-1.5">
        {DAY_ORDER.map(d => (
          <div key={d} className="grid grid-cols-[80px_1fr] items-center gap-2">
            <span className="text-xs">{d}</span>
            <input value={hours[d] || ""} onChange={e => onChange({ ...hours, [d]: e.target.value })}
              className="wofy-input !py-1 text-sm" placeholder="e.g. 9am–6pm  or  Closed"
              data-testid={`hours-${d}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CAD", "AUD", "SGD", "AED"];

function MenuEditor({ menu, onChange }) {
  const [sectionInput, setSectionInput] = useState("");

  const addItem = (section) => {
    const nextOrder = menu.length ? Math.max(...menu.map(m => m.order || 0)) + 1 : 0;
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
    onChange([...menu, {
      id, section: section || "Menu", name: "", description: "", price: null,
      currency: "USD", photo_url: "", tags: [], available: true, order: nextOrder,
    }]);
  };

  const patchItem = (id, patch) => {
    onChange(menu.map(m => m.id === id ? { ...m, ...patch } : m));
  };

  const removeItem = (id) => {
    if (!confirm("Remove this menu item?")) return;
    onChange(menu.filter(m => m.id !== id));
  };

  const sections = [...new Set(menu.map(m => m.section || "Menu"))];
  const grouped = {};
  for (const s of sections) grouped[s] = menu.filter(m => (m.section || "Menu") === s);

  return (
    <div className="space-y-6" data-testid="tab-menu-content">
      <div className="wofy-card !p-4">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Add a new menu section</div>
        <div className="flex gap-2">
          <input value={sectionInput} onChange={e => setSectionInput(e.target.value)}
            placeholder="e.g. Starters, Mains, Desserts, Drinks"
            className="wofy-input flex-1" data-testid="menu-section-input" />
          <button type="button" onClick={() => { if (sectionInput.trim()) { addItem(sectionInput.trim()); setSectionInput(""); } }}
            className="wofy-btn-primary inline-flex items-center gap-1 text-sm" data-testid="menu-add-section">
            <Plus className="w-4 h-4" />Add
          </button>
        </div>
        <p className="text-xs opacity-60 mt-2">Or add items directly to an existing section below.</p>
      </div>

      {menu.length === 0 && (
        <div className="wofy-card !p-6 text-center opacity-70" data-testid="menu-empty">
          <Utensils className="w-8 h-8 mx-auto opacity-40 mb-2" />
          <div className="text-sm">No menu items yet.</div>
          <button type="button" onClick={() => addItem("Menu")}
            className="mt-3 wofy-btn-primary inline-flex items-center gap-1 text-sm" data-testid="menu-add-first">
            <Plus className="w-4 h-4" />Add first item
          </button>
        </div>
      )}

      {sections.map(sec => (
        <div key={sec} className="wofy-card !p-4 space-y-3" data-testid={`menu-editor-section-${sec}`}>
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{sec}</h3>
            <span className="text-[10px] font-mono opacity-60">{grouped[sec].length} items</span>
            <button type="button" onClick={() => addItem(sec)}
              className="ml-auto text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5"
              style={{ borderColor: "var(--wofy-border)" }} data-testid={`menu-add-to-${sec}`}>
              <Plus className="w-3.5 h-3.5" />Add item
            </button>
          </div>
          <div className="space-y-3">
            {grouped[sec].map(it => (
              <div key={it.id} className="rounded-lg border p-3 grid gap-2 md:grid-cols-[1fr_120px_auto]"
                   style={{ borderColor: "var(--wofy-border)" }} data-testid={`menu-editor-item-${it.id}`}>
                <div className="space-y-2">
                  <input value={it.name || ""} onChange={e => patchItem(it.id, { name: e.target.value })}
                    placeholder="Item name" className="wofy-input" data-testid={`menu-name-${it.id}`} />
                  <textarea rows={2} value={it.description || ""} onChange={e => patchItem(it.id, { description: e.target.value })}
                    placeholder="Short description (optional)" className="wofy-input text-sm" data-testid={`menu-desc-${it.id}`} />
                  <div className="flex gap-2">
                    <input value={it.photo_url || ""} onChange={e => patchItem(it.id, { photo_url: e.target.value })}
                      placeholder="Photo URL (optional)" className="wofy-input text-xs flex-1" data-testid={`menu-photo-${it.id}`} />
                    <input value={(it.tags || []).join(", ")}
                      onChange={e => patchItem(it.id, { tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean).slice(0, 6) })}
                      placeholder="Tags (comma-sep)" className="wofy-input text-xs w-40" data-testid={`menu-tags-${it.id}`} />
                  </div>
                  <input value={it.section || sec} onChange={e => patchItem(it.id, { section: e.target.value })}
                    placeholder="Section" className="wofy-input text-xs" data-testid={`menu-section-${it.id}`} />
                </div>
                <div className="space-y-2">
                  <div className="flex gap-1">
                    <select value={it.currency || "USD"} onChange={e => patchItem(it.id, { currency: e.target.value })}
                      className="wofy-input !py-1 text-xs w-20" data-testid={`menu-cur-${it.id}`}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" step="0.01" value={it.price ?? ""}
                      onChange={e => patchItem(it.id, { price: e.target.value === "" ? null : parseFloat(e.target.value) })}
                      placeholder="0.00" className="wofy-input !py-1 text-sm flex-1" data-testid={`menu-price-${it.id}`} />
                  </div>
                  <label className="inline-flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={it.available !== false}
                      onChange={e => patchItem(it.id, { available: e.target.checked })} data-testid={`menu-avail-${it.id}`} />
                    Available
                  </label>
                </div>
                <button type="button" onClick={() => removeItem(it.id)}
                  className="self-start text-red-600 hover:bg-red-50 rounded p-1.5" data-testid={`menu-remove-${it.id}`} title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="text-xs opacity-60">
        Menu items are shown on your public site in the order you add them. Uncheck "Available" to keep an item on file but hidden from customers.
      </p>
    </div>
  );
}
